import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Payments, Save, Sell } from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import type { ExpensesMonthly, FixedExpenseRow, OneTimeExpenseRow } from '../types';
import { formatDay, formatMoney } from '../components/commercial/contact';
import ExpenseDialog, { todayIso } from '../components/expenses/ExpenseDialog';
import PayExpenseDialog from '../components/expenses/PayExpenseDialog';
import ExpensesMonthlyView from '../components/expenses/ExpensesMonthlyView';
import BudgetPanel from '../components/expenses/BudgetPanel';

type TabKey = 'dia' | 'fijos' | 'deudas' | 'mes' | 'presupuesto';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'dia', label: 'Del día a día' },
  { key: 'fijos', label: 'Fijos' },
  { key: 'deudas', label: 'A pagar' },
  { key: 'mes', label: 'Mes a mes' },
  { key: 'presupuesto', label: 'Presupuesto' },
];

const monthRange = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
  return { from: `${month}-01`, to: `${next}-01` };
};

const statusChip = (e: OneTimeExpenseRow) => {
  if (!e.isPayable || e.balance <= 0.01) return <Chip size="small" color="success" variant="outlined" label="Pagado" />;
  const overdue = e.dueDate && e.dueDate.slice(0, 10) < todayIso();
  return <Chip size="small" color={overdue ? 'error' : 'warning'} label={`Debe ${formatMoney(e.balance)} · vence ${formatDay(e.dueDate)}`} />;
};

/** Editor de la lista de categorías (compartida por gastos fijos, del día a día y presupuesto). */
const CategoriesDialog: React.FC<{ open: boolean; categories: string[]; onClose: () => void; onSaved: (c: string[]) => void }> = ({ open, categories, onClose, onSaved }) => {
  const [items, setItems] = useState<string[]>([]);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setItems(categories); setNewName(''); setError(null); } }, [open, categories]);
  const add = () => {
    const n = newName.trim();
    if (!n) return;
    if (items.some(i => i.toLowerCase() === n.toLowerCase())) { setError('Esa categoría ya está.'); return; }
    setItems(x => [...x, n]);
    setNewName('');
    setError(null);
  };
  const save = async () => {
    try { onSaved(await api.setExpenseCategories(items)); onClose(); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo guardar'); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Categorías de gasto</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se usan en los gastos fijos, en los del día a día y en el presupuesto. Quitar una no borra los gastos que ya la tienen.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField size="small" label="Nueva categoría" value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') add(); }} sx={{ flex: 1 }} inputProps={{ maxLength: 50 }} />
          <Button onClick={add} startIcon={<Add />}>Agregar</Button>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {items.map(c => <Chip key={c} label={c} onDelete={() => setItems(x => x.filter(y => y !== c))} />)}
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={items.length === 0}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

export const ExpensesPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab: TabKey = (TABS.find(t => t.key === params.get('tab'))?.key) || 'dia';
  const setTab = (t: TabKey) => setParams(p => { const n = new URLSearchParams(p); n.set('tab', t); return n; }, { replace: true });

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  // Día a día
  const [month, setMonth] = useState(todayIso().slice(0, 7));
  const [oneTime, setOneTime] = useState<OneTimeExpenseRow[] | null>(null);
  const [dialog, setDialog] = useState<{ expense: OneTimeExpenseRow | null; payable: boolean } | null>(null);
  const [paying, setPaying] = useState<OneTimeExpenseRow | null>(null);

  // A pagar
  const [debts, setDebts] = useState<OneTimeExpenseRow[] | null>(null);

  // Fijos
  const [fixed, setFixed] = useState<FixedExpenseRow[]>([]);
  const [fxName, setFxName] = useState('');
  const [fxAmount, setFxAmount] = useState<number>(0);
  const [fxCategory, setFxCategory] = useState('General');
  const [fxDay, setFxDay] = useState<number>(10);

  // Mes a mes
  const [months, setMonths] = useState(12);
  const [monthly, setMonthly] = useState<ExpensesMonthly | null>(null);

  const loadOneTime = useCallback(async () => {
    setOneTime(null);
    try { setOneTime(await api.getOneTimeExpenses(monthRange(month))); } catch { setOneTime([]); }
  }, [month]);
  const loadDebts = useCallback(async () => {
    try { setDebts(await api.getOneTimeExpenses({ pendingOnly: true })); } catch { setDebts([]); }
  }, []);

  useEffect(() => {
    api.getExpenseCategories().then(setCategories).catch(() => setCategories(['General']));
    api.getFixedExpenses().then(setFixed).catch(() => setFixed([]));
    loadDebts();
  }, [loadDebts]);
  useEffect(() => { loadOneTime(); }, [loadOneTime]);
  useEffect(() => {
    if (tab !== 'mes') return;
    setMonthly(null);
    api.getExpensesMonthly(months).then(setMonthly).catch(() => setError('No se pudo cargar el mes a mes'));
  }, [tab, months]);

  const totalFixed = useMemo(() => fixed.filter(f => f.isActive).reduce((s, f) => s + (f.amountMonthly || 0), 0), [fixed]);
  const totalMonth = useMemo(() => (oneTime || []).reduce((s, o) => s + (o.amount || 0), 0), [oneTime]);
  const totalDebt = useMemo(() => (debts || []).reduce((s, o) => s + (o.balance || 0), 0), [debts]);

  const onSaved = () => { loadOneTime(); loadDebts(); };

  const removeOneTime = async (o: OneTimeExpenseRow) => {
    if (!window.confirm(`¿Borrar "${o.description}"?`)) return;
    setSaving(true);
    try { await api.deleteOneTimeExpense(o.id); onSaved(); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo borrar'); }
    finally { setSaving(false); }
  };

  const addFixed = async () => {
    if (!fxName.trim() || fxAmount <= 0) return;
    setSaving(true);
    try {
      const created = await api.createFixedExpense({ name: fxName.trim(), amountMonthly: fxAmount, isActive: true, category: fxCategory, paymentDueDay: fxDay });
      setFixed(prev => [created, ...prev]);
      setFxName(''); setFxAmount(0);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al crear gasto fijo');
    } finally { setSaving(false); }
  };

  const expenseRows = (rows: OneTimeExpenseRow[], empty: string) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fecha</TableCell>
            <TableCell>Descripción</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Categoría</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Proveedor</TableCell>
            <TableCell align="right">Monto</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(o => (
            <TableRow key={o.id} hover>
              <TableCell>{formatDay(o.date)}</TableCell>
              <TableCell>{o.description}{o.notes ? <Typography variant="caption" color="text.secondary" display="block">{o.notes}</Typography> : null}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{o.category}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{o.supplierName || '—'}</TableCell>
              <TableCell align="right">{formatMoney(o.amount)}</TableCell>
              <TableCell>{statusChip(o)}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {o.isPayable && o.balance > 0.01 && (
                  <Tooltip title="Registrar pago"><IconButton size="small" color="primary" onClick={() => setPaying(o)}><Payments fontSize="small" /></IconButton></Tooltip>
                )}
                <Tooltip title="Editar"><IconButton size="small" onClick={() => setDialog({ expense: o, payable: o.isPayable })}><Edit fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Borrar"><span><IconButton size="small" disabled={saving} onClick={() => removeOneTime(o)}><Delete fontSize="small" /></IconButton></span></Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">{empty}</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Gastos</Typography>
          <Typography variant="body2" color="text.secondary">
            Fijos, del día a día y deudas con proveedores. Todo impacta en el Flujo de Caja y en el beneficio del mes.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<Sell />} onClick={() => setCategoriesOpen(true)}>Categorías</Button>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setDialog({ expense: null, payable: true })}>Cargar deuda</Button>
          <Button variant="contained" size="large" startIcon={<Add />} onClick={() => setDialog({ expense: null, payable: false })}>Cargar gasto</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Fijos / mes</Typography>
          <Typography variant="h6" fontWeight={600}>{formatMoney(totalFixed)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Día a día (mes elegido)</Typography>
          <Typography variant="h6" fontWeight={600}>{formatMoney(totalMonth)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Deudas a pagar</Typography>
          <Typography variant="h6" fontWeight={600} color={totalDebt > 0 ? 'warning.main' : undefined}>{formatMoney(totalDebt)}</Typography>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {TABS.map(t => <Tab key={t.key} value={t.key} label={t.key === 'deudas' && debts?.length ? `${t.label} (${debts.length})` : t.label} />)}
        </Tabs>
        <CardContent>
          {tab === 'dia' && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <TextField size="small" type="month" label="Mes" value={month} onChange={e => setMonth(e.target.value)} InputLabelProps={{ shrink: true }} />
                <Typography variant="body2" color="text.secondary">Nafta, etiquetas, fletes, reparaciones… lo que se gasta una vez.</Typography>
              </Stack>
              {oneTime == null
                ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
                : expenseRows(oneTime, 'No hay gastos cargados en este mes. Tocá "Cargar gasto".')}
            </Box>
          )}

          {tab === 'deudas' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Facturas de proveedores que todavía no pagaste (gráfica, flete, diseño…). Las deudas por compras con orden de compra
                están en <Link component={RouterLink} to="/debt-ranking">Orden de pago</Link>, junto con estas.
              </Typography>
              {debts == null
                ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
                : expenseRows(debts, 'No hay deudas pendientes. Para cargar una, tocá "Cargar deuda".')}
            </Box>
          )}

          {tab === 'fijos' && (
            <Box>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
                <TextField size="small" label="Nombre" value={fxName} onChange={e => setFxName(e.target.value)} sx={{ minWidth: 200 }} placeholder="Contador, banco, web…" />
                <TextField size="small" select label="Categoría" value={fxCategory} onChange={e => setFxCategory(e.target.value)} sx={{ minWidth: 150 }}>
                  {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="Monto mensual" value={fxAmount || ''} onChange={e => setFxAmount(Number(e.target.value || 0))} sx={{ minWidth: 150 }} />
                <TextField size="small" type="number" label="Vence el día" value={fxDay} onChange={e => setFxDay(Math.min(31, Math.max(1, Number(e.target.value || 1))))} sx={{ width: 120 }} />
                <Button startIcon={<Add />} variant="contained" disabled={!fxName.trim() || fxAmount <= 0 || saving} onClick={addFixed}>
                  Agregar
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Categoría</TableCell>
                      <TableCell align="right">Monto mensual</TableCell>
                      <TableCell align="right">Vence el día</TableCell>
                      <TableCell align="center">Activo</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fixed.map(e => {
                      const set = (patch: Partial<FixedExpenseRow>) => setFixed(prev => prev.map(x => x.id === e.id ? { ...x, ...patch } : x));
                      const options = categories.includes(e.category) ? categories : [...categories, e.category];
                      return (
                        <TableRow key={e.id}>
                          <TableCell><TextField size="small" value={e.name} onChange={ev => set({ name: ev.target.value })} /></TableCell>
                          <TableCell>
                            <TextField size="small" select value={e.category} onChange={ev => set({ category: ev.target.value })} sx={{ minWidth: 130 }}>
                              {options.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                          </TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number" value={e.amountMonthly} onChange={ev => set({ amountMonthly: Number(ev.target.value || 0) })} sx={{ width: 140 }} />
                          </TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number" value={e.paymentDueDay} onChange={ev => set({ paymentDueDay: Math.min(31, Math.max(1, Number(ev.target.value || 1))) })} sx={{ width: 80 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Switch checked={e.isActive} onChange={(_, v) => set({ isActive: v })} />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Guardar"><span>
                                <IconButton disabled={saving} onClick={async () => {
                                  setSaving(true);
                                  try {
                                    const upd = await api.updateFixedExpense(e.id, { name: e.name, amountMonthly: e.amountMonthly, isActive: e.isActive, category: e.category, paymentDueDay: e.paymentDueDay });
                                    setFixed(prev => prev.map(x => x.id === e.id ? upd : x));
                                  } finally { setSaving(false); }
                                }}><Save /></IconButton>
                              </span></Tooltip>
                              <Tooltip title="Eliminar"><span>
                                <IconButton disabled={saving} onClick={async () => {
                                  if (!window.confirm('¿Eliminar gasto fijo?')) return;
                                  setSaving(true);
                                  try { await api.deleteFixedExpense(e.id); setFixed(prev => prev.filter(x => x.id !== e.id)); }
                                  finally { setSaving(false); }
                                }}><Delete /></IconButton>
                              </span></Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {fixed.length === 0 && (
                      <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No hay gastos fijos cargados.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Los vencimientos de los fijos aparecen en el Calendario y en el Flujo de Caja.
              </Typography>
            </Box>
          )}

          {tab === 'mes' && (
            <Box>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                <TextField select size="small" value={months} onChange={e => setMonths(Number(e.target.value))} sx={{ minWidth: 200 }}>
                  {[6, 12, 24].map(m => <MenuItem key={m} value={m}>Últimos {m} meses</MenuItem>)}
                </TextField>
              </Stack>
              {monthly ? <ExpensesMonthlyView data={monthly} /> : <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
            </Box>
          )}

          {tab === 'presupuesto' && <BudgetPanel categories={categories} />}
        </CardContent>
      </Card>

      <ExpenseDialog
        open={!!dialog}
        expense={dialog?.expense}
        defaultPayable={dialog?.payable}
        categories={categories}
        onClose={() => setDialog(null)}
        onSaved={onSaved}
      />
      <PayExpenseDialog expense={paying} onClose={() => setPaying(null)} onPaid={onSaved} />
      <CategoriesDialog open={categoriesOpen} categories={categories} onClose={() => setCategoriesOpen(false)} onSaved={setCategories} />
    </Box>
  );
};

export default ExpensesPage;
